import React, { memo } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Chip,
} from "@mui/material";
import { FileTextIcon } from "lucide-react";

const getConditionColor = (requirementType) => {
  if (requirementType === "MANDATORY") return "#dc2626";
  if (requirementType === "OPTIONAL") return "#f59e0b";
  return "#0891b2";
};

const getConditionBgColor = (requirementType) => {
  if (requirementType === "MANDATORY") return "#fee2e2";
  if (requirementType === "OPTIONAL") return "#fef3c7";
  return "#cffafe";
};

const getConditionLabel = (requirementType, onlyOneRequired) => {
  if (onlyOneRequired) return "At least one mandatory";
  if (requirementType === "MANDATORY") return "Mandatory";
  if (requirementType === "OPTIONAL") return "Optional";
  return "Conditional";
};

const DocumentCategoryCard = memo(
  ({ category, subtext, documents, icon: Icon }) => {
    return (
      <Card
        sx={{
          mb: 3,
          // border: "1px solid #e5e7eb",
          borderRadius: "16px",
          boxShadow: "none",
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {Icon ? (
                <Icon size={24} style={{ color: "#2563eb" }} />
              ) : (
                <FileTextIcon size={24} style={{ color: "#2563eb" }} />
              )}
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#1f2937" }}
                >
                  {category}
                </Typography>
                {subtext && (
                  <Typography variant="caption" sx={{ color: "#6b7280" }}>
                    {subtext}
                  </Typography>
                )}
              </Box>
            </Box>
          }
          action={
            <Chip
              label={`${documents.length} document${
                documents.length > 1 ? "s" : ""
              }`}
              variant="outlined"
              size="small"
            />
          }
          sx={{
            backgroundColor: "#f9fafb",
            borderBottom: "1px solid #e5e7eb",
            py: 2,
          }}
        />
        <CardContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {documents.map((doc, idx) => (
              <Box
                key={doc.id}
                sx={{
                  display: "flex",
                  gap: 2,
                  pb: 2,
                  borderBottom:
                    idx === documents.length - 1 ? "none" : "1px solid #f3f4f6",
                }}
              >
                {/* Step Number */}
                <Box
                  sx={{
                    minWidth: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    backgroundColor: "#e0f2fe",
                    color: "#0369a1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "16px",
                    flexShrink: 0,
                  }}
                >
                  {idx + 1}
                </Box>

                {/* Document Info */}
                <Box sx={{ flex: 1, pt: 0.5 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: "#1f2937" }}
                    >
                      {doc.checklistLabel || doc.name}
                    </Typography>
                    {doc.subtext && (
                      <Typography variant="caption" sx={{ color: "#6b7280" }}>
                        {doc.subtext}
                      </Typography>
                    )}
                  </Box>

                  <Chip
                    label={getConditionLabel(
                      doc.requirementType,
                      doc.onlyOneRequired,
                    )}
                    size="small"
                    sx={{
                      height: "24px",
                      backgroundColor: getConditionBgColor(doc.requirementType),
                      color: getConditionColor(doc.requirementType),
                      fontWeight: 600,
                      fontSize: "12px",
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  },
);

DocumentCategoryCard.displayName = "DocumentCategoryCard";

export default DocumentCategoryCard;
